import { Tower } from './Tower';
import { Monster } from '../monsters/Monster';
import { AudioManager, SoundEffect } from '../../systems/AudioManager';

export class LaserTower extends Tower {
  private chargingEffect: Phaser.GameObjects.Graphics | null = null;
  private laserArray: Phaser.GameObjects.Graphics[] = [];

  constructor(scene: Phaser.Scene, x: number, y: number) {
    super(scene, x, y, 60, 2.5, 120);
  }

  protected override createVisuals(): void {
    // Call parent satellite structure
    super.createVisuals();
    
    // Override with laser-specific satellite design
    this.createLaserSatelliteSpecific();
  }

  private createLaserSatelliteSpecific(): void {
    // Remove default communication array and replace with laser arrays
    this.removeAll();
    this.createSatelliteStructure();
    this.createSolarPanels();
    this.createLaserArrays(); // Laser-specific
    this.createTargetingSystem(); // Laser-specific
    this.createThrusters();
    this.createEnergyCore();
    this.createOrbitalMechanics();
  }

  private createLaserArrays(): void {
    // Primary laser array (main weapon)
    const primaryArray = this.scene.add.graphics();
    
    // Laser assembly housing
    primaryArray.fillStyle(0x34495e, 1);
    primaryArray.lineStyle(1, 0x2c3e50, 1);
    primaryArray.fillRect(-6, -30, 12, 15);
    primaryArray.strokeRect(-6, -30, 12, 15);
    
    // Laser emitter crystals
    primaryArray.fillStyle(0xe74c3c, 0.8);
    primaryArray.fillCircle(-3, -25, 2);
    primaryArray.fillCircle(3, -25, 2);
    primaryArray.fillCircle(0, -22, 2);
    
    // Crystal glow
    primaryArray.lineStyle(1, 0xff6b6b, 0.8);
    primaryArray.strokeCircle(-3, -25, 2);
    primaryArray.strokeCircle(3, -25, 2);
    primaryArray.strokeCircle(0, -22, 2);
    
    // Focusing lens
    primaryArray.fillStyle(0x74b9ff, 0.6);
    primaryArray.fillRect(-4, -35, 8, 3);
    primaryArray.lineStyle(1, 0x3498db, 1);
    primaryArray.strokeRect(-4, -35, 8, 3);
    
    this.add(primaryArray);
    this.laserArray?.push(primaryArray);
    
    // Secondary laser pods (support arrays)
    for (let i = 0; i < 4; i++) {
      const angle = (Math.PI * 2 * i) / 4 + Math.PI / 4;
      const distance = 20;
      const x = Math.cos(angle) * distance;
      const y = Math.sin(angle) * distance;
      
      const laserPod = this.scene.add.graphics();
      
      // Pod housing
      laserPod.fillStyle(0x2c3e50, 1);
      laserPod.lineStyle(1, 0x34495e, 1);
      laserPod.fillEllipse(0, 0, 8, 6);
      laserPod.strokeEllipse(0, 0, 8, 6);
      
      // Mini laser emitter
      laserPod.fillStyle(0xe74c3c, 0.6);
      laserPod.fillCircle(0, -2, 1.5);
      laserPod.lineStyle(0.5, 0xff6b6b, 0.8);
      laserPod.strokeCircle(0, -2, 1.5);
      
      laserPod.setPosition(x, y);
      laserPod.setRotation(angle);
      this.add(laserPod);
      this.laserArray?.push(laserPod);
    }
    
    // Crystal charging animation
    this.scene.tweens.add({
      targets: primaryArray,
      alpha: 0.7,
      duration: 800,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut'
    });
  }

  private createTargetingSystem(): void {
    // Advanced targeting scanner
    const scanner = this.scene.add.graphics();
    
    // Scanner dish
    scanner.fillStyle(0x95a5a6, 1);
    scanner.lineStyle(1, 0xbdc3c7, 1);
    scanner.fillEllipse(0, 10, 14, 6);
    scanner.strokeEllipse(0, 10, 14, 6);
    
    // Targeting laser pointer
    scanner.fillStyle(0xe74c3c, 1);
    scanner.fillCircle(0, 10, 1);
    
    // Scanner array grid
    scanner.lineStyle(0.5, 0x3498db, 0.6);
    for (let i = -5; i <= 5; i += 2) {
      scanner.lineBetween(i, 7, i, 13);
    }
    for (let i = 7; i <= 13; i += 2) {
      scanner.lineBetween(-6, i, 6, i);
    }
    
    this.add(scanner);
    
    // Fast targeting sweep
    this.scene.tweens.add({
      targets: scanner,
      rotation: Math.PI * 2,
      duration: 3000,
      repeat: -1,
      ease: 'Linear'
    });
    
    // Targeting pulse
    this.scene.time.addEvent({
      delay: 1000,
      callback: () => this.createTargetingPulse(),
      loop: true
    });
  }

  private createTargetingPulse(): void {
    const pulse = this.scene.add.graphics();
    pulse.lineStyle(2, 0xe74c3c, 0.8);
    pulse.strokeCircle(0, 0, 5);
    this.add(pulse);
    
    this.scene.tweens.add({
      targets: pulse,
      scaleX: 4,
      scaleY: 4,
      alpha: 0,
      duration: 1000,
      ease: 'Power2.easeOut',
      onComplete: () => pulse.destroy()
    });
  }

  protected createLaserChargingEffect(): void {
    if (this.chargingEffect) {
      this.chargingEffect.destroy();
    }

    this.chargingEffect = this.scene.add.graphics();
    
    // Charging energy buildup in laser arrays
    this.laserArray.forEach((array, index) => {
      const chargeGlow = this.scene.add.graphics();
      chargeGlow.fillStyle(0xe74c3c, 0.3);
      chargeGlow.fillCircle(array.x, array.y, 15 + (index * 2));
      chargeGlow.lineStyle(2, 0xff6b6b, 0.8);
      chargeGlow.strokeCircle(array.x, array.y, 15 + (index * 2));
      
      this.add(chargeGlow);
      
      this.scene.tweens.add({
        targets: chargeGlow,
        scaleX: 1.5,
        scaleY: 1.5,
        alpha: 0,
        duration: 500,
        ease: 'Power2.easeOut',
        onComplete: () => chargeGlow.destroy()
      });
    });
  }
} 